import React, {FC, useEffect, useState, useMemo, useRef} from 'react';

import {Box, useTheme} from '@mui/material';
import {If, Then, Else} from 'react-if';

import MapMarker from 'assets/images/mapMarker.svg';
import {Loading} from 'components/loader/Loading';
import {config} from 'config/env';
import {ANALYTICS} from 'contexts/analytics-context';
import {debounce} from 'utils/helper';

interface staticMapProps {
  addressString?: string;
  latLng?: {
    lat: number;
    lng: number;
  };
  onAddressChange?: () => void;
  addAddressEvent: (eventName: string, payload: any) => void;
}

export const StaticMap: FC<staticMapProps> = ({addressString, latLng, onAddressChange, addAddressEvent}) => {
  const [mapImageUri, setMapImageUri] = useState('');
  const theme = useTheme();

  const addAddressEventRef = useRef(addAddressEvent);

  useEffect(() => {
    addAddressEventRef.current = addAddressEvent;
  }, [addAddressEvent]);

  const debouncedApiCall = useMemo(() => {
    return debounce((newAddressString) => {
      const uri = `https://maps.googleapis.com/maps/api/staticmap?center=${newAddressString}&zoom=16&size=546x180&maptype=roadmap&key=${config.STATIC_GOOGLE_MAP_API_KEY}`;
      setMapImageUri(uri);
      addAddressEventRef.current(ANALYTICS.ADDRESS_DECODED, {type: 'staticmap', googleUrl: uri});
    }, 300);
  }, []);

  useEffect(() => {
    const init = async () => {
      if (addressString) {
        onAddressChange && onAddressChange();
      }
      const newAddressString = latLng ? `${latLng.lat},${latLng.lng}` : addressString;
      if (newAddressString) {
        debouncedApiCall(newAddressString);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latLng, addressString]);

  return (
    <If condition={!!mapImageUri}>
      <Then>
        <div style={{position: 'relative', display: 'inline-block', width: '100%'}}>
          <img
            src={mapImageUri}
            alt="Static map"
            style={{display: 'block', width: '-webkit-fill-available', maxHeight: '180px'}}
          />
          <Box
            style={{
              width: theme.spacing(7),
              height: theme.spacing(7),
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img src={MapMarker} alt="mapmarker-icon" />
          </Box>
        </div>
      </Then>
      <Else>
        <Loading />
      </Else>
    </If>
  );
};
